// La `ref` de idempotencia de las apuestas (HAL-03).
//
// Desde el #282 `/api/apuesta`, `/api/betslip` y `/api/box/{agencia}/betslip`
// aceptan una `ref` en el cuerpo (`_idem_abrir` en casino_api.py, hasta 80
// caracteres): si el mismo pedido llega dos veces con la misma `ref`, el
// servidor devuelve el boleto original en vez de crear otro y debitar de
// nuevo. Sirve para el caso que importa: el pedido llegó, el boleto se creó,
// pero la respuesta se perdió en el camino y el jugador toca otra vez.
//
// La regla es una `ref` por INTENCIÓN, no por pedido:
//   · se crea la primera vez que se manda ese boleto;
//   · se REUSA mientras lo que se manda sea idéntico (el reintento);
//   · se suelta con el éxito, así jugar dos veces lo mismo a propósito —el
//     "Mantener selecciones" del Box— son dos boletos, como debe ser;
//   · si el boleto cambió (otro pick, otro monto), es otra `ref` sola: se
//     compara el cuerpo entero, así nadie tiene que acordarse de soltarla.
//
// Vive a nivel de módulo, por pantalla (`sitio`), y no en el estado de React:
// los ayudantes de App.jsx que mandan la apuesta no son componentes.

export const MENSAJE_REF_USADA =
  "Ese pedido ya se usó con otra apuesta. Volvé a armar el boleto.";
export const MENSAJE_EN_CURSO =
  "Tu apuesta anterior todavía se está procesando. Esperá unos segundos y probá de nuevo.";

const pendientes = new Map();

// `randomUUID` solo existe en contexto seguro y en navegadores recientes; los
// WebView viejos de Telegram tienen `getRandomValues` y nada más.
export function nuevaRef(cripto = typeof window !== "undefined" ? window.crypto : undefined) {
  if (cripto && typeof cripto.randomUUID === "function") return cripto.randomUUID();
  if (cripto && typeof cripto.getRandomValues === "function") {
    const bytes = cripto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  // Sin ninguna de las dos: peor que una ref de verdad, mejor que ninguna. Un
  // choque acá termina en un 409, nunca en el boleto de otro (lo frena la
  // huella del servidor).
  return Date.now().toString(36) + Math.random().toString(36).slice(2)
    + Math.random().toString(36).slice(2);
}

// Varios boletos en un mismo envío (las simples del sitio): cada uno con su
// ref, y el reintento del envío entero reusa todas. Así, si se cortó en el
// segundo, el primero vuelve como el boleto que ya existe y no se duplica.
export function refsDelEnvio(sitio, cuerpos) {
  const huella = JSON.stringify(cuerpos);
  let pendiente = pendientes.get(sitio);
  if (!pendiente || pendiente.huella !== huella) {
    pendiente = { huella, refs: cuerpos.map(() => nuevaRef()) };
    pendientes.set(sitio, pendiente);
  }
  return cuerpos.map((cuerpo, i) => ({ ...cuerpo, ref: pendiente.refs[i] }));
}

export function conRef(sitio, cuerpo) {
  return refsDelEnvio(sitio, [cuerpo])[0];
}

// Se llama cuando el envío terminó bien: la próxima apuesta es otra.
export function envioCumplido(sitio) {
  pendientes.delete(sitio);
}

// El servidor contesta 409 en dos casos (`_idem_previa` en casino_api.py):
//   · la ref ya se usó con OTRO boleto: reusarla falla siempre, así que se
//     suelta y el próximo intento lleva una nueva;
//   · el pedido original todavía está en vuelo: se CONSERVA, porque el
//     reintento con la misma ref es justo lo que devuelve ese boleto. Soltarla
//     acá crearía el duplicado que todo esto evita.
// Devuelve "" si no es un 409, para encadenar con el mensaje que ya había.
export function mensajeDeConflicto(sitio, status, detail) {
  if (status !== 409) return "";
  if (typeof detail === "string" && /en curso/i.test(detail)) return MENSAJE_EN_CURSO;
  envioCumplido(sitio);
  return MENSAJE_REF_USADA;
}
